import { transactionsRepository } from "@/database/repositories/transactionsRepository";
import { Transaction } from "@/database/schema";
import { useEffect, useState } from "react";
import { $customers, $invoices, $transactions } from "@/stores";
import { ConfirmDelete } from "@/shared/confirmDelete/ConfirmDelete";
import { Flip, toast, ToastContainer } from "react-toastify";
import { invoicesRepository } from "@/database/repositories/invoicesRepository";
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    TableMeta,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import "./Transactions.scss";
import { transactionsColumns } from "@/components/transactions/TransactionsColumns";
import { DataTableToolbar } from "@/shared/table/TableToolbar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/table/TableElements";
import { DataTablePagination } from "@/shared/table/TablePagination";
import { customersRepository } from "@/database/repositories/customersRepository";
import { Trash2 } from "lucide-react";
import { TableFilterOption } from "@/types";
import { useSearchParams } from "react-router-dom";
import "../../shared/table/Table.scss";
import { useDb } from "@/database/provider/useDb";
import { Spinner } from "@/shared/spinner/Spinner";

export interface TransactionsTableMeta extends TableMeta<Transaction> {
    deleteTransaction: (id: number, invoiceNumber: string) => Promise<void>
}

export const Transactions = () => {
    const db = useDb();
    const [searchParams] = useSearchParams();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [deleteManyOpen, setDeleteManyOpen] = useState(false);
    const [filterCustomers, setFilterCustomers] = useState<TableFilterOption[]>([]);
    const [filterInvoices, setFilterInvoices] = useState<TableFilterOption[]>([]);
    const [rowSelection, setRowSelection] = useState({});
    const [columnSizing, setColumnSizing] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [openOptionsId, setOpenOptionsId] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [invoicesLoading, setInvoicesLoading] = useState(true);

    const deleteTransaction = async (id: number, invoiceNumber: string) => {
        try {
            await transactionsRepository.delete(db, id);

            const transactions = $transactions.value.values.filter((item) => item.id !== id);
            setTransactions(transactions);
            $transactions.set({ isLoad: true, values: transactions });

            const state = await invoicesRepository.updateState(db, invoiceNumber);

            const invoices = $invoices.value.values;
            if (invoices.length > 0) {
                const invoice = invoices.find((item) => item.invoiceNumber === invoiceNumber);
                if (invoice) {
                    invoice.status = state;
                    invoice.transactions = invoice.transactions.filter((item) => item.id !== id);
                }
            }

            toast.success(`Transaction deleted!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            throw new Error(err.message)
        }
    }
    const deleteMany = async () => {
        try {
            const ids = table.getSelectedRowModel().rows.map(row => row.original.id);
            const deleted = await transactionsRepository.deleteMany(db, ids);

            const uniqueDeleted = deleted.filter(
                (item, index, self) => self.findIndex(t => t.invoiceNumber === item.invoiceNumber) === index
            );
            for (const { invoiceNumber, id } of uniqueDeleted) {
                const state = await invoicesRepository.updateState(db, invoiceNumber);
                const invoice = $invoices.value.values.find((item) => item.invoiceNumber === invoiceNumber);
                if (invoice) {
                    invoice.status = state;
                    invoice.transactions = invoice.transactions.filter((item) => item.id !== id);
                }
            }

            const transactions = await transactionsRepository.getAll(db);
            setTransactions(transactions);
            $transactions.set({ isLoad: true, values: transactions });
            setDeleteManyOpen(false);
            toast.success("Transactions deleted!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }
    const table = useReactTable({
        data: transactions,
        columns: transactionsColumns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing
        },
        meta: {
            type: "Transactions",
            openOptionsId,
            setOpenOptionsId,
            deleteTransaction
        } as TransactionsTableMeta,
        enableRowSelection: true,
        onColumnSizingChange: setColumnSizing,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    useEffect(() => {
        try {
            if (!$transactions.value.isLoad) {
                transactionsRepository.getAll(db).then((res) => {
                    setTransactions(res);
                    $transactions.set({ isLoad: true, values: res });
                });
            }
            else {
                setIsLoading(false);
                setTransactions($transactions.value.values)
            };
            async function setFilters() {
                if ($customers.value.isLoad) setFilterCustomers($customers.value.values.map(({ name }) => {
                    return { text: name, value: name };
                }));
                else {
                    const customers = await customersRepository.getAll(db);
                    const customersFilters = customers.map(({ name }) => {
                        return { text: name, value: name };
                    });
                    setFilterCustomers(customersFilters);
                }

                if ($invoices.value.isLoad) {
                    setFilterInvoices($invoices.value.values.map(({ invoiceNumber }) => {
                        return { text: `Invoice ${invoiceNumber}`, value: invoiceNumber };
                    }));
                }
                else {
                    const invoices = await invoicesRepository.getAllUnArchived(db);
                    const invoicesFilters = invoices.map(({ invoiceNumber }) => {
                        return { text: `Invoice ${invoiceNumber}`, value: invoiceNumber };
                    });
                    setFilterInvoices(invoicesFilters);
                    $invoices.set({ isLoad: true, values: invoices });
                }
                setCustomersLoading(false);
                setInvoicesLoading(false);
            }
            setFilters();

            const customersFilters = searchParams.get("customer");
            const invoicesFilters = searchParams.get("invoice");

            if (customersFilters) {
                const customersArray = customersFilters.split(",");
                const column = table.getColumn("customer");
                column?.setFilterValue(customersArray);
            }
            if (invoicesFilters) {
                const invoicesArray = invoicesFilters.split(",");
                const column = table.getColumn("invoiceNumber");
                column?.setFilterValue(invoicesArray);
            }
            const timeout = setTimeout(() => {
                setIsLoading(false);
            }, 600);

            return () => clearTimeout(timeout);
        }
        catch (err: any) {
            throw new Error(err.message)
        }
    }, []);

    return <section className="transactions">

        <div className="transactions__container">
            {deleteManyOpen && <div className="overlay">
                <ConfirmDelete
                    type="Transaction"
                    action="Delete"
                    count={table.getSelectedRowModel().rows.length}
                    onClose={() => { setDeleteManyOpen(false) }}
                    onDelete={deleteMany} />
            </div>}
            <ToastContainer />

            <div className="table-container">
                <div className="table-container__data">
                    <div className="table-container__head">
                        <div className="table__head-container">
                            <h1>Transactions</h1>
                        </div>
                    </div>
                    {(!customersLoading && !invoicesLoading && !isLoading) ? <>
                        <div className="table__toolbar-container">
                            <DataTableToolbar
                                table={table}
                                filtersFields={[
                                    { field: "customer", title: "Customer", options: filterCustomers },
                                    { field: "invoiceNumber", title: "Invoice", options: filterInvoices }
                                ]}
                                searchField="id" />
                            {table.getSelectedRowModel().rows.length > 0 &&
                                <button className="table__delete-button" onClick={() => {
                                    setDeleteManyOpen(true);
                                }}>
                                    <Trash2 width={14} height={14} /> Delete {table.getSelectedRowModel().rows.length} items
                                </button>
                            }
                        </div>

                        <div className="table-container__table-area">
                            <Table>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                return (
                                                    <TableHead key={header.id} style={{ width: `${header.getSize()}%` }}>
                                                        {header.isPlaceholder ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </TableHead>
                                                )
                                            })}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                onClick={() => {
                                                    if (table.getSelectedRowModel().rows.length > 0) {
                                                        return row.toggleSelected()
                                                    }
                                                }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id} className="table__cell"
                                                        style={{ width: `${cell.column.getSize()}%` }}
                                                        onClick={(e) => {
                                                            const { id } = cell.column;
                                                            if (id === "select" || id === "actions") e.stopPropagation()
                                                        }}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <div className="empty">
                                            <div className="empty__inner">
                                                <img src="/Eyes.png" alt="No Data" />
                                                <b>No results</b>
                                            </div>
                                        </div>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </> : <div className="spinner-container">
                        <Spinner />
                    </div>}
                </div>
                <div className="table-container__pagination">
                    <DataTablePagination table={table} />
                </div>
            </div>

        </div>
    </section >
}